import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Adp } from '@adsk/adp-web-analytics-sdk';
import { FLAG_NAMES } from '../constants';
import { initializeCjs } from '../helpers/cjsAnalytics';
import { useLdFlags } from '../hooks';
import { LocaleContext } from './LocaleContext';

const AdpAnalyticsContext = createContext();

const hasGrantedAnalyticsConsent = (preferences) =>
  Array.isArray(preferences) &&
  preferences.length > 0 &&
  !preferences.some(
    ({ optIn }) => optIn === null || optIn === undefined
  ) &&
  preferences.some(({ optIn }) => optIn === true);

const CjsInitializer = ({
  adp,
  env,
  isConsentGranted,
  isReadyToTrack,
  productId,
}) => {
  const flags = useLdFlags();
  const { locale } = useContext(LocaleContext) || {};
  const isCjsEnabled = flags[FLAG_NAMES.UI_PROFILE_CJS_ENABLED];

  useEffect(() => {
    if (
      !adp ||
      env === 'dev' ||
      !isCjsEnabled ||
      !isReadyToTrack ||
      !isConsentGranted
    ) {
      return;
    }

    initializeCjs({
      adp,
      env,
      productId,
      pageLocale: locale,
    }).catch((error) => {
      console.log(`${error}: CJS SDK was not initialized!`);
    });
  }, [
    adp,
    env,
    isCjsEnabled,
    isConsentGranted,
    isReadyToTrack,
    locale,
    productId,
  ]);

  return null;
};

export const AdpAnalyticsProvider = ({
  env,
  productId,
  productName,
  accessToken,
  appId,
  disableAutoPageCapture = false,
  enableCjsInstrumentation = false,
  children,
}) => {
  const [isReadyToTrack, setIsReadyToTrack] = useState(false);
  const [isCjsConsentGranted, setIsCjsConsentGranted] = useState(false);

  const adp = useMemo(() => {
    // Skip DEV env since we will not be able to verify events
    if (env === 'dev') {
      console.log('Dev would be skipped!');
      return;
    }

    // Get adp instance
    const adpEnv = env === 'prd' ? 'prd' : 'stg';
    const adpClient = Adp().setEnv(adpEnv);
    if (adpEnv !== 'prd') {
      adpClient.enableDebug();
    }

    /*
     ** Load & setup the Web SDK
     ** https://pages.git.autodesk.com/cloudplatform-bigdata/adp-web-analytics-sdk/classes/AdpAnalytics.html#load
     ** productId => app client id
     */
    const productInfo = {
      id: productId,
      id_provider: 'appkey',
      name: productName, 
    };

    adpClient.load(productInfo);

    return adpClient;
  }, [env, productId, productName]);

  useEffect(() => {
    if (!adp || !enableCjsInstrumentation) return undefined;

    let isConsentListenerActive = true;
    adp.on('user_consent', (context) => {
      if (isConsentListenerActive) {
        setIsCjsConsentGranted(
          hasGrantedAnalyticsConsent(context?.userConsentPreferences)
        );
      }
    });

    return () => {
      isConsentListenerActive = false;
    };
  }, [adp, enableCjsInstrumentation]);

  useEffect(() => {
    if (!adp) return;

    const loadUserConsent = async () => {
      try {
        const result = await adp.loadUserConsentPreferences(accessToken);
        const consentStatus = result?.status;
        if (!consentStatus) {
          throw result instanceof Error
            ? result
            : new Error('User consent status is missing');
        }
        if (consentStatus.code === consentStatus.PENDING_USER_ACTION) {
          adp.showUserConsentDialog();
        }
        if (enableCjsInstrumentation) {
          setIsCjsConsentGranted(
            consentStatus.code === consentStatus.USER_OPT_IN
          );
        }

        adp.identify();
        if (disableAutoPageCapture) {
          adp.disableAutoPageCapture();
        }
        adp.enableAutoCaptureClickEvents();
        // Connecting parent app with MFE. Parent app should call setHostProduct() method 
        adp.enableHostProductData(appId);
        setIsReadyToTrack(true);
      } catch (error) {
        // TODO: we should send some logs to Splunk in the future 
        console.log(`${error}: User Consent preferences was not loaded!`); 
      }
    }

    loadUserConsent();
  }, [adp, accessToken, enableCjsInstrumentation]);

  return (
    <AdpAnalyticsContext.Provider value={isReadyToTrack ? adp : null}>
      {/* profile-mfe also uses this provider and remains ADP-only. */}
      {enableCjsInstrumentation && (
        <CjsInitializer
          adp={adp}
          env={env}
          isConsentGranted={isCjsConsentGranted}
          isReadyToTrack={isReadyToTrack}
          productId={productId}
        />
      )}
      {children}
    </AdpAnalyticsContext.Provider>
  );
};

// Custom hook
export const useAdpAnalytics = () => {
  const context = useContext(AdpAnalyticsContext);
  return context;
};
